// Self-heal helper (#1080): a deterministic escape from a stale-bundle crash on
// the live Site. Apply any waiting service worker and hard-reload onto fresh
// code, so the "Try Again" button stops re-running the same broken bundle.
//
// Plain `.ts` (no React, no firebase import) so it unit-tests cleanly and dodges
// both `react-refresh/only-export-components` and the ts-jest `import.meta` trap
// (see key-patterns-web). Deps are injected with global-reading defaults so the
// call site stays a bare `applyWaitingSwAndReload()`.

// How long to wait for `controllerchange` after activating the waiting worker
// before reloading anyway. Real activation fires well within this.
const CONTROLLERCHANGE_FALLBACK_MS = 3000;

const defaultServiceWorker = (): ServiceWorkerContainer | undefined =>
  typeof navigator !== "undefined" ? navigator.serviceWorker : undefined;

export const applyWaitingSwAndReload = async (
  sw: ServiceWorkerContainer | undefined = defaultServiceWorker(),
  reload: () => void = () => window.location.reload(),
): Promise<void> => {
  // Per-invocation guard: the controllerchange listener and the timeout
  // fallback race to reload — only the first wins.
  let reloaded = false;
  const reloadOnce = (): void => {
    if (reloaded) return;
    reloaded = true;
    reload();
  };

  // No SW (dev / devOptions.enabled:false / E2E build / unsupported) — a plain
  // reload still re-fetches index.html + assets, which is the point.
  if (!sw) {
    reloadOnce();
    return;
  }

  const reg = await sw.getRegistration();
  if (!reg) {
    reloadOnce();
    return;
  }

  await reg.update();

  if (reg.waiting) {
    // Reload once the new worker takes control of the page.
    sw.addEventListener("controllerchange", reloadOnce, { once: true });
    reg.waiting.postMessage({ type: "SKIP_WAITING" });
    // Fallback in case controllerchange never fires.
    setTimeout(reloadOnce, CONTROLLERCHANGE_FALLBACK_MS);
  } else {
    // No waiting worker — a plain reload still re-fetches fresh assets after a
    // deploy (the common stale-bundle case).
    reloadOnce();
  }
};
